import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import './assets/theme.css';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useAppSettingsStore } from './stores/appSettings';
import i18nPlugin from './i18n';
import useApi from './composables/useApi';
import { getClientSessionId, getCurrentPagePath } from './utils/analytics';
import { initializeTheme } from './utils/theme';

if (typeof window !== 'undefined') {
  initializeTheme();
}

const VisibilityOverview = () => import(/* webpackChunkName: "visibility" */ './components/pages/visibilities/VisibilityOverview.vue');
const ReportVisibility = () => import(/* webpackChunkName: "visibility" */ './components/pages/visibilities/ReportVisibility.vue');
const AddDiveSite = () => import(/* webpackChunkName: "dive-sites" */ './components/pages/diveSites/AddDiveSite.vue');
const ShowDiveSite = () => import(/* webpackChunkName: "dive-sites" */ './components/pages/diveSites/ShowDiveSite.vue');
const MainView = () => import(/* webpackChunkName: "main" */ './components/pages/MainView.vue');
const AddWaterBody = () => import(/* webpackChunkName: "water-bodies" */ './components/pages/waterBodies/AddWaterBody.vue');
const AddFacility = () => import(/* webpackChunkName: "facilities" */ './components/pages/facilities/AddFacility.vue');
const AddBoat = () => import(/* webpackChunkName: "boats" */ './components/pages/boats/AddBoat.vue');
const AdminPage = () => import(/* webpackChunkName: "admin" */ './components/pages/AdminPage.vue');
const AdminUserDetails = () => import(/* webpackChunkName: "admin" */ './components/pages/AdminUserDetails.vue');
const AdminAnalyticsPage = () => import(/* webpackChunkName: "admin" */ './components/pages/AdminAnalyticsPage.vue');
const AboutPage = () => import(/* webpackChunkName: "main" */ './components/pages/AboutPage.vue');
const LoginUser = () => import(/* webpackChunkName: "auth" */ './components/pages/auth/LoginUser');
const RegisterUser = () => import(/* webpackChunkName: "auth" */ './components/pages/auth/RegisterUser');
const UserView = () => import(/* webpackChunkName: "auth" */ './components/pages/auth/UserView.vue');
const ForgotPassword = () => import(/* webpackChunkName: "auth" */ './components/pages/auth/ForgotPassword.vue');
const ResetPassword = () => import(/* webpackChunkName: "auth" */ './components/pages/auth/ResetPassword.vue');

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: MainView, name: 'home', meta: { requiresPublicAppAccess: true } },
    { path: '/about', component: AboutPage, name: 'about' },
    { path: '/visibilities', component: VisibilityOverview, name: 'visibility-overview', meta: { requiresPublicAppAccess: true } },
    { path: '/visibilities/report', component: ReportVisibility, name: 'report-visibility', meta: { requiresPublicAppAccess: true } },
    { path: '/dive-sites/create', component: AddDiveSite, name: 'add-dive-site', meta: { requiresDiveSiteEditor: true } },
    { path: '/dive-sites/:id', component: ShowDiveSite, name: 'show-dive-site', meta: { requiresPublicAppAccess: true } },
    { path: '/water-bodies/create', component: AddWaterBody, name: 'add-water-body' },
    { path: '/water-bodies/:id/edit', component: AddWaterBody, name: 'edit-water-body', meta: { requiresAdmin: true } },
    { path: '/facilities/create', component: AddFacility, name: 'add-facility' },
    { path: '/facilities/:id/edit', component: AddFacility, name: 'edit-facility', meta: { requiresAdmin: true } },
    { path: '/boats/create', component: AddBoat, name: 'add-boat', meta: { requiresAdmin: true } },
    { path: '/boats/:id/edit', component: AddBoat, name: 'edit-boat', meta: { requiresAdmin: true } },
    { path: '/login', component: LoginUser },
    { path: '/register', component: RegisterUser },
    { path: '/forgot-password', component: ForgotPassword },
    { path: '/reset-password', component: ResetPassword },
    { path: '/user', component: UserView },
    { path: '/admin', component: AdminPage, meta: { requiresAdmin: true } },
    { path: '/admin/stats', component: AdminAnalyticsPage, meta: { requiresAdmin: true } },
    { path: '/admin/users/:id', component: AdminUserDetails, meta: { requiresAdmin: true } }
  ],
});

const app = createApp(App)
const pinia = createPinia();
app.use(pinia);
app.use(i18nPlugin);
useApi();

const authStore = useAuthStore(pinia);
const appSettingsStore = useAppSettingsStore(pinia);

function startPresenceTracking(routerInstance) {
  if (typeof window === 'undefined') return;

  const api = useApi();
  const clientSessionId = getClientSessionId();
  if (!clientSessionId) return;

  let isSending = false;

  const sendHeartbeat = async () => {
    if (isSending) return;

    isSending = true;
    try {
      await api.post('/api/v1/analytics/activity', {
        sessionId: clientSessionId,
        path: getCurrentPagePath()
      });
    } catch {
      // Ignore analytics failures so they never block the app.
    } finally {
      isSending = false;
    }
  };

  const sendVisibleHeartbeat = () => {
    if (document.visibilityState === 'hidden') return;
    void sendHeartbeat();
  };

  routerInstance.afterEach(() => {
    void sendHeartbeat();
  });

  window.addEventListener('focus', sendVisibleHeartbeat);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void sendHeartbeat();
    }
  });

  window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void sendHeartbeat();
    }
  }, 60000);

  void sendHeartbeat();
}

router.beforeEach(async (to) => {
  if (typeof window !== 'undefined' && !['/login', '/register', '/forgot-password', '/reset-password'].includes(to.path)) {
    window.sessionStorage.setItem('postLoginRedirect', to.fullPath || '/');
  }

  if (to.meta?.requiresDiveSiteEditor) {
    if (!authStore.isAuthenticated) {
      return { path: '/login', query: { redirect: to.fullPath || '/' } };
    }

    if (!authStore.role) {
      try {
        await authStore.getUser();
      } catch {
        return { path: '/login', query: { redirect: to.fullPath || '/' } };
      }
    }

    if (['contributor', 'admin', 'superuser'].includes(authStore.role)) {
      return true;
    }

    return { path: '/' };
  }

  if (to.meta?.requiresPublicAppAccess && appSettingsStore.requireLoginForPublicApp && !authStore.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath || '/' } };
  }

  if (!to.meta?.requiresAdmin) return true;

  if (!authStore.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath || '/' } };
  }

  if (!authStore.role) {
    try {
      await authStore.getUser();
    } catch {
      return { path: '/login', query: { redirect: to.fullPath || '/' } };
    }
  }

  if (authStore.role === 'admin' || authStore.role === 'superuser') {
    return true;
  }

  return { path: '/' };
});

async function bootstrap() {
  await authStore.bootstrapAuth();
  try {
    await appSettingsStore.loadPublicSettings();
  } catch {
    // Keep the public app available if settings cannot be loaded.
  }
  app.use(router);
  app.mount('#app');
  startPresenceTracking(router);
}

bootstrap();
